import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, MailX, Mail } from 'lucide-react';
import { newsletterApi } from '../services/api';
import { NewsletterSignupForm } from '../components/NewsletterSignup';

const Shell = ({ children, testId }) => (
  <div className="min-h-[70vh] flex items-start sm:items-center justify-center px-4 py-10 bg-[#faf7f1]" data-testid={testId}>
    <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-6 sm:p-8 text-neutral-900">{children}</div>
  </div>
);

export function NewsletterJoinPage() {
  return (
    <Shell testId="nl-join-page">
      <div className="inline-flex items-center gap-2 text-green-700 text-xs font-semibold uppercase tracking-wider"><Mail size={14} /> Weekly newsletter</div>
      <h1 className="mt-2 text-3xl font-bold">Join Our Weekly Newsletter</h1>
      <p className="mt-2 text-sm text-neutral-700">Black business news, events, deals and community wins from Promote Black Business — once a week. No membership needed.</p>
      <div className="mt-6"><NewsletterSignupForm /></div>
    </Shell>
  );
}

export function NewsletterConfirmPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    newsletterApi.confirm(params.get('token') || '').then(r => setState({ ok: true, name: r.firstName }))
      .catch(e => setState({ error: e?.response?.data?.detail || 'This link is invalid or expired.' }));
  }, [params]);
  return (
    <Shell testId="nl-confirm-page">
      {state.loading ? <Loader2 className="animate-spin mx-auto text-neutral-400" /> : state.ok ? (
        <div className="text-center" data-testid="nl-confirm-success">
          <CheckCircle2 className="mx-auto text-green-600" size={44} />
          <h1 className="mt-3 text-2xl font-bold">You're subscribed{state.name ? `, ${state.name}` : ''}!</h1>
          <p className="mt-2 text-sm text-neutral-700">Welcome to the PBB newsletter. Look for it in your inbox every week.</p>
          <Link to="/" className="inline-block mt-5 px-5 py-2.5 rounded-full bg-green-600 text-white font-semibold">Visit Promote Black Business</Link>
        </div>
      ) : <div className="text-center text-sm text-neutral-700" data-testid="nl-confirm-error">{state.error}</div>}
    </Shell>
  );
}

export function NewsletterUnsubscribePage() {
  const [params] = useSearchParams();
  const [state, setState] = useState('ask');
  const go = async () => {
    setState('busy');
    try { await newsletterApi.unsubscribe({ e: params.get('e') || '', t: params.get('t') || '', c: params.get('c') }); setState('done'); }
    catch { setState('error'); }
  };
  return (
    <Shell testId="nl-unsub-page">
      <div className="text-center">
        <MailX className="mx-auto text-neutral-500" size={40} />
        {state === 'done' ? (
          <div data-testid="nl-unsub-done">
            <h1 className="mt-3 text-2xl font-bold">You've been unsubscribed</h1>
            <p className="mt-2 text-sm text-neutral-700">You won't receive the PBB newsletter anymore. Changed your mind? You can sign up again any time.</p>
            <Link to="/newsletter/join" className="inline-block mt-5 text-green-700 font-semibold">Sign up again</Link>
          </div>
        ) : state === 'error' ? (
          <p className="mt-3 text-sm text-red-600" data-testid="nl-unsub-error">This unsubscribe link isn't valid. Please use the link from your newsletter email.</p>
        ) : (
          <>
            <h1 className="mt-3 text-2xl font-bold">Unsubscribe from the PBB newsletter?</h1>
            <p className="mt-2 text-sm text-neutral-700">You'll stop receiving the weekly newsletter. Account emails like receipts aren't affected.</p>
            <button onClick={go} disabled={state === 'busy'} className="mt-5 px-5 py-2.5 rounded-full bg-neutral-900 text-white font-semibold inline-flex items-center gap-2" data-testid="nl-unsub-confirm">
              {state === 'busy' && <Loader2 className="animate-spin" size={14} />} Unsubscribe
            </button>
          </>
        )}
      </div>
    </Shell>
  );
}
